import { requestFitplus } from '../api';
import type {
    ManagingPageContentsInfo,
    TimeSeriesResDto,
    TrendReportManagingPageResDto,
    TrendReportResDto,
} from '@common/openapi-client/openapi';
import type {
    PatchModuleDataInSheetPayload,
    RegenerateModulePayload,
    TrendReportFilterRequestBody,
    DeleteModuleDataInSheetPayload,
} from '../types/proReportAdmin';
import type { ApiResponse } from '../types/response';

const endPoint = {
    GET_TREND_REPORT_PAGE: 'backoffice/trend-report-page?_Page=#PAGE#&_PageSize=#PAGE_SIZE#',
    POST_TREND_REPORT_PAGE_WITH_FILTER: 'backoffice/get-trend-report-page?_Page=#PAGE#&_PageSize=#PAGE_SIZE#',
    POST_TREND_REPORT_PAGE_WITH_FILTER_ADMIN:
        'backoffice/trend-report/get-trend-report-page?_Page=#PAGE#&_PageSize=#PAGE_SIZE#',
    GET_TOTAL_PAGE: 'backoffice/total-page?_PageSize=#PAGE_SIZE#',
    POST_GET_TOTAL_PAGE_ADMIN: 'backoffice/trend-report/get-total-page?_PageSize=#PAGE_SIZE#',
    GET_REPORT_EXISTING_END_DATES: 'backoffice/trend-report/report-existing-end-dates?UserUID=#USER_UID#',
    GET_TIMESERIES:
        'backoffice/trend-report/time-series?UserUID=#USER_UID#&EndDate=#END_DATE#&ReportType=#REPORT_TYPE#',
    GET_MODULE_DATA:
        'backoffice/trend-report/module-data?UserUID=#USER_UID#&EndDate=#END_DATE#&ReportType=#REPORT_TYPE#&Version=#VERSION#',
    PATCH_CHANGE_ISPUBLISH:
        'backoffice/trend-report/change-ispublish?_UID=#USER_UID#&_reportType=#REPORT_TYPE#&_GenerationDate=#GENERATION_DATE#&_Version=#VERSION#&ReportCode=#REPORT_CODE#',
    PATCH_CHANGE_ISHUMANCONFIRMED:
        'backoffice/trend-report/change-ishumanconfirmed?_UID=#USER_UID#&_ReportType=#REPORT_TYPE#&_GenerationDate=#GENERATION_DATE#&_Version=#VERSION#&reportCode=#REPORT_CODE#',
    PATCH_MODULE_DATA_IN_SHEET: 'backoffice/trend-report/module-data-in-sheet',
    DELETE_MODULE_DATA_IN_SHEET: 'backoffice/trend-report/module-data-in-sheet',
    POST_REGENERATE_MODULE_DATA:
        'backoffice/trend-report/regenerate-module-data?UID=#UID#&ReportType=#REPORT_TYPE#&GenerationDate=#GENERATION_DATE#&Version=#VERSION#&ModuleIndex=#MODULE_INDEX#&EndDateTime=#END_DATE_TIME#&ReportCode=#REPORT_CODE#',
    PATCH_CHANGE_ISHAVETOREVIEW:
        'backoffice/trend-report/change-ishavetoreview?_UID=#USER_UID#&_reportType=#REPORT_TYPE#&_GenerationDate=#GENERATION_DATE#&_Version=#VERSION#&ReportCode=#REPORT_CODE#',
    GET_MODULE_DATA_ONLY_PUBLISHED:
        'backoffice/trend-report/module-data-only-published?UserUID=#USER_UID#&EndDate=#END_DATE#',
};

export const getTrendReportPageApi = (
    page: number,
    pageSize: number,
): Promise<ApiResponse<TrendReportManagingPageResDto>> => {
    const url = endPoint.GET_TREND_REPORT_PAGE.replace('#PAGE#', page.toString()).replace(
        '#PAGE_SIZE#',
        pageSize.toString(),
    );
    return requestFitplus<TrendReportManagingPageResDto>(url, 'get', undefined);
};

// 필터가 추가된 리포트 페이지 조회
export const postTrendReportPageWithFilterApi = (
    page: number,
    pageSize: number,
    filter: TrendReportFilterRequestBody,
): Promise<ApiResponse<TrendReportManagingPageResDto>> => {
    const url = endPoint.POST_TREND_REPORT_PAGE_WITH_FILTER_ADMIN.replace('#PAGE#', page.toString()).replace(
        '#PAGE_SIZE#',
        pageSize.toString(),
    );
    return requestFitplus<TrendReportManagingPageResDto>(url, 'post', filter);
};

// 전체 데이터 페이지 수 조회
export const getTotalPageApi = (pageSize: number): Promise<ApiResponse<number>> => {
    const url = endPoint.GET_TOTAL_PAGE.replace('#PAGE_SIZE#', pageSize.toString());
    return requestFitplus<number>(url, 'get', undefined, {
        isAdminToken: true,
    });
};

// 필터링 된 데이터 페이지 수 조회
export const postGetTotalPageApi = (
    pageSize: number,
    filter: TrendReportFilterRequestBody,
): Promise<ApiResponse<number>> => {
    const url = endPoint.POST_GET_TOTAL_PAGE_ADMIN.replace('#PAGE_SIZE#', pageSize.toString());
    return requestFitplus<number>(url, 'post', filter, {
        isAdminToken: true,
    });
};

export const getTimeseriesDataApi = (
    userUid: string,
    endDate: string,
    reportType: string,
): Promise<ApiResponse<TimeSeriesResDto>> => {
    const url = endPoint.GET_TIMESERIES.replace('#USER_UID#', userUid)
        .replace('#END_DATE#', endDate)
        .replace('#REPORT_TYPE#', reportType);
    return requestFitplus<TimeSeriesResDto>(url, 'get', undefined, {
        isAdminToken: true,
    });
};

export const getModuleDataApi = (
    userUid: string,
    endDate: string,
    version: number,
    reportType: string,
): Promise<ApiResponse<TrendReportResDto>> => {
    const url = endPoint.GET_MODULE_DATA.replace('#USER_UID#', userUid)
        .replace('#END_DATE#', endDate)
        .replace('#REPORT_TYPE#', reportType)
        .replace('#VERSION#', version.toString());
    return requestFitplus<TrendReportResDto>(url, 'get', undefined, {
        isAdminToken: true,
    });
};

export const patchChangeIsPublishApi = (
    userUid: string,
    reportType: string,
    generationDate: string,
    version: string,
    reportCode: string,
): Promise<ApiResponse<ManagingPageContentsInfo>> => {
    const url = endPoint.PATCH_CHANGE_ISPUBLISH.replace('#USER_UID#', userUid)
        .replace('#REPORT_TYPE#', reportType)
        .replace('#GENERATION_DATE#', generationDate)
        .replace('#VERSION#', version)
        .replace('#REPORT_CODE#', reportCode);
    return requestFitplus<ManagingPageContentsInfo>(url, 'patch', undefined, {
        isAdminToken: true,
    });
};

// 수동컨펌하기
export const patchChangeIsHumanConfirmedApi = (
    userUid: string,
    reportType: string,
    generationDate: string,
    version: string,
    reportCode: string,
): Promise<ApiResponse<ManagingPageContentsInfo>> => {
    // GenerationDate를 'YYYY-MM-DD' 형태로 변환 (ISO 형식에서 날짜 부분만 추출)
    const formattedGenerationDate = generationDate.includes('T') ? generationDate.split('T')[0] : generationDate;
    const url = endPoint.PATCH_CHANGE_ISHUMANCONFIRMED.replace('#USER_UID#', userUid)
        .replace('#REPORT_TYPE#', reportType)
        .replace('#GENERATION_DATE#', formattedGenerationDate)
        .replace('#VERSION#', version)
        .replace('#REPORT_CODE#', reportCode);
    return requestFitplus<ManagingPageContentsInfo>(url, 'patch', undefined, {
        isAdminToken: true,
    });
};

export const postRegenerateModuleDataApi = (
    payload: RegenerateModulePayload,
): Promise<ApiResponse<ManagingPageContentsInfo>> => {
    const url = endPoint.POST_REGENERATE_MODULE_DATA.replace('#UID#', payload.UID)
        .replace('#REPORT_TYPE#', payload.ReportType)
        .replace('#GENERATION_DATE#', payload.GenerationDate)
        .replace('#VERSION#', payload.Version)
        .replace('#MODULE_INDEX#', payload.ModuleIndex)
        .replace('#END_DATE_TIME#', payload.EndDate)
        .replace('#REPORT_CODE#', payload.ReportCode ?? '');
    return requestFitplus<ManagingPageContentsInfo>(url, 'post', undefined, {
        isAdminToken: true,
    });
};

export const patchModuleDataInSheetApi = (
    payload: PatchModuleDataInSheetPayload,
): Promise<ApiResponse<ManagingPageContentsInfo>> => {
    const { UID, ReportType, GenerationDate, EndDate, Version, ModuleIndex, Title, Body, FitscoreEval } = payload;
    const params = {
        UID,
        ReportType,
        GenerationDate,
        EndDate,
        Version,
        ModuleIndex,
        Title,
        Body,
        FitscoreEval,
    };
    return requestFitplus<ManagingPageContentsInfo>(endPoint.PATCH_MODULE_DATA_IN_SHEET, 'patch', params, {
        isAdminToken: true,
    });
};

// 리포트 특정 모듈 숨김/보임 토글 처리
export const deleteModuleDataInSheetApi = (
    payload: DeleteModuleDataInSheetPayload,
): Promise<ApiResponse<ManagingPageContentsInfo>> => {
    const { UID, ReportType, ReportCode, EndDate, Version, ModuleIndex } = payload;
    const params = {
        UID,
        ReportType,
        ReportCode,
        EndDate,
        Version,
        ModuleIndex,
    };
    return requestFitplus<ManagingPageContentsInfo>(endPoint.DELETE_MODULE_DATA_IN_SHEET, 'delete', params, {
        isAdminToken: true,
    });
};

export const getReportExistingEndDatesApi = (userUid: string): Promise<ApiResponse<string[]>> => {
    const url = endPoint.GET_REPORT_EXISTING_END_DATES.replace('#USER_UID#', userUid);
    return requestFitplus<string[]>(url, 'get', undefined, {
        isAdminToken: true,
    });
};

export const patchChangeIsHaveToReviewApi = (
    userUid: string,
    reportType: string,
    generationDate: string,
    version: string,
    reportCode: string,
): Promise<ApiResponse<ManagingPageContentsInfo>> => {
    const url = endPoint.PATCH_CHANGE_ISHAVETOREVIEW.replace('#USER_UID#', userUid)
        .replace('#REPORT_TYPE#', reportType)
        .replace('#GENERATION_DATE#', generationDate)
        .replace('#VERSION#', version)
        .replace('#REPORT_CODE#', reportCode);
    return requestFitplus<ManagingPageContentsInfo>(url, 'patch', undefined, {
        isAdminToken: true,
    });
};

export const getModuleDataOnlyPublishedApi = (
    userUid: string,
    endDate: string,
): Promise<ApiResponse<TrendReportResDto>> => {
    const url = endPoint.GET_MODULE_DATA_ONLY_PUBLISHED.replace('#USER_UID#', userUid).replace('#END_DATE#', endDate);
    return requestFitplus<TrendReportResDto>(url, 'get', undefined, {
        isAdminToken: true,
    });
};
